import { loadBaseCSS } from '../shared/button-utils.js';
import { upgradeProperty } from '../shared/form-control.js';
import { primitiveStyles } from '../shared/primitive-utils.js';

/**
 * A labelled tab owned by its direct `qds-tabs` parent. Do not nest interactive content.
 * @element qds-tab
 * @attr {string} value - Stable value matching a qds-tab-panel.
 * @attr {boolean} disabled - Excludes this tab from selection and keyboard navigation.
 * @property {string} value - Stable tab identity.
 * @property {boolean} disabled - Whether the tab can be activated.
 * @slot default - Plain text or decorative icon and label.
 * @csspart label - Label container.
 */
export class QdsTab extends HTMLElement {
  static get observedAttributes(): string[] { return ['value', 'disabled']; }
  constructor() {
    super();
    this.attachShadow({ mode: 'open' }).innerHTML = `<style>${primitiveStyles}
      :host { display: inline-flex; align-items: center; justify-content: center; min-height: 44px;
        min-width: 44px; padding: 8px 16px; cursor: pointer; border-block-end: 2px solid transparent;
        border-radius: var(--theme-radius-sm, 4px) var(--theme-radius-sm, 4px) 0 0; }
      :host(:hover) { background: var(--theme-background-hover, rgba(0,0,0,.05)); }
      :host([aria-selected="true"]) { border-color: var(--theme-accent-primary, #445e89); font-weight: 600; }
      :host([disabled]) { cursor: not-allowed; color: var(--theme-text-tertiary, #6a6a6a); }
      @media (forced-colors: active) { :host([aria-selected="true"]) { border-color: Highlight; } }
    </style><span part="label"><slot></slot></span>`;
    loadBaseCSS();
  }
  connectedCallback(): void {
    upgradeProperty(this, 'value'); upgradeProperty(this, 'disabled');
    if (!this.hasAttribute('slot')) this.slot = 'tab';
    this.setAttribute('role', 'tab');
    if (!this.hasAttribute('tabindex')) this.tabIndex = -1;
    this.attributeChangedCallback();
  }
  attributeChangedCallback(): void {
    if (this.isConnected) this.setAttribute('aria-disabled', String(this.disabled));
  }
  get value(): string { return this.getAttribute('value') || ''; }
  set value(value: string) { this.setAttribute('value', value ?? ''); }
  get disabled(): boolean { return this.hasAttribute('disabled'); }
  set disabled(value: boolean) { this.toggleAttribute('disabled', Boolean(value)); }
}
if (!customElements.get('qds-tab')) customElements.define('qds-tab', QdsTab);
